import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { FieldGroup, FormBuilder, FieldControl, Validators } from 'react-reactive-form';
import { useAppContext } from '../contexts/AppContext';
import { markAllControlsDirtyAndTouched, printInvalidControls } from '../helpers/form.helper';
import { closePopupIcon, passwordEyeIcon } from '../assets/superAdminAssets';
import { Constants } from '../constants';
import { sortSelectList } from '../helpers/index.helper';
import { getSMTPDetailSystemParams, updateSMTPDetailSystemParams } from '../services/smtp.service';

const portValidator = (control) => {
    if (!control.value) {
        return null;
    }
    const portNum = parseInt(control.value, 10);
    if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
        return { invalidPort: true };
    }
    return null;
};

const hostValidator = (control) => {
    if (!control.value) {
        return null;
    }
    const hostValue = control.value.trim();
    if (hostValue.length === 0) {
        return { invalidHost: true };
    }
    return null;
};

const PROVIDERS = {
    SMTP: 'SMTP',
    GOOGLE: 'Gmail',
    MICROSOFT: 'Microsoft'
}

const emailProviderOptions = [
    { value: PROVIDERS.MICROSOFT, label: 'Microsoft 365 / Outlook' },
    { value: PROVIDERS.GOOGLE, label: 'Google Workspace / Gmail' },
    // { value: PROVIDERS.SMTP, label: 'SMTP Server' }
];

const SMTPSettings = ({ show, onClose, companyId, companyName }) => {
    const [showAnimation, setShowAnimation] = useState(false);
    const { showLoader, hideLoader, showToaster, hideToaster } = useAppContext();
    const [selectedEmailProvider, setSelectedEmailProvider] = useState(emailProviderOptions.find(p => p.value === PROVIDERS.GOOGLE)); // Default to SMTP

    useEffect(() => {
        let timer;
        if (show) {
            fetchEmailSettings();
            timer = setTimeout(() => {
                setShowAnimation(true);
            }, 50);
        }
        setShowAnimation(false);
        return () => clearTimeout(timer);
    }, [show, companyId]);

    const emailForm = useMemo(() => FormBuilder.group({
        // SMTP
        smtpHost: [PROVIDERS.GOOGLE],
        smtpPort: [''],
        smtpUsername: [''],
        smtpPassword: [''],
        useTLS: [false],
        // Google (SMTP)
        googleAuthType: [''],
        googleClientId: [''],
        googleClientSecret: [''],
        // Microsoft
        microsoftAuthType: [''],
        microsoftClientId: [''],
        microsoftClientSecret: [''],
        microsoftTenantId: ['',]
    }), []);

    const showPassword = (inputId, eyeId) => {
        const input = document.getElementById(inputId);
        const eyeIcon = document.getElementById(eyeId);
        if (!input || !eyeIcon) return;
        input.type = input.type === 'password' ? 'text' : 'password';
        eyeIcon.classList.toggle('active_pass');
    };

    // Helper to (un)set validators per control
    const setControlRequired = (controlName, required) => {
        const control = emailForm.get(controlName);
        if (!control) return;
        if (required) {
            control.setValidators([Validators.required, controlName === 'smtpPort' ? portValidator : controlName === 'smtpHost' ? hostValidator : null].filter(Boolean));
        } else {
            control.clearValidators();
        }
        control.updateValueAndValidity();
    };

    const applyValidatorsForProvider = (provider) => {
        // Reset all validators first
        const allControls = ['smtpHost', 'smtpPort', 'smtpUsername', 'smtpPassword', 'googleClientId', 'googleClientSecret', 'microsoftClientId', 'microsoftClientSecret', 'microsoftTenantId', 'googleAuthType', 'microsoftAuthType'];
        allControls.forEach(c => setControlRequired(c, false));

        if (provider === PROVIDERS.SMTP) {
            ['smtpHost', 'smtpPort', 'smtpUsername', 'smtpPassword'].forEach(c => setControlRequired(c, true));
            emailForm.reset({
                provider: provider,
                useTLS: true,
                smtpHost: emailForm.value.smtpHost || '',
                smtpPort: emailForm.value.smtpPort || '',
                smtpUsername: emailForm.value.smtpUsername || '',
                smtpPassword: emailForm.value.smtpPassword || '',
            });
        } else if (provider === PROVIDERS.GOOGLE) {
            // Using SMTP credentials for Gmail
            ['smtpHost', 'smtpPort', 'smtpUsername', 'smtpPassword'].forEach(c => setControlRequired(c, true));
            emailForm.reset({
                provider: provider,
                useTLS: true,
                smtpHost: emailForm.value.smtpHost || '',
                smtpPort: emailForm.value.smtpPort || '',
                smtpUsername: emailForm.value.smtpUsername || '',
                smtpPassword: emailForm.value.smtpPassword || '',
            });
        } else if (provider === PROVIDERS.MICROSOFT) {
            ['microsoftClientId', 'microsoftTenantId', 'microsoftClientSecret', 'smtpUsername'].forEach(c => setControlRequired(c, true));
            emailForm.reset({
                provider: provider,
                useTLS: true,
                microsoftClientId: emailForm.value.microsoftClientId || '',
                microsoftTenantId: emailForm.value.microsoftTenantId || '',
                microsoftClientSecret: emailForm.value.microsoftClientSecret || '',
                smtpUsername: emailForm.value.smtpUsername || '',
            });
        }
    };

    const handleSave = async () => {
        try {
            if (!companyId) {
                showToaster("Company ID is required", 'error');
                return;
            }
            markAllControlsDirtyAndTouched(emailForm);
            if (!emailForm.valid) {
                printInvalidControls(emailForm);
                return;
            }
            showLoader();
            const data = {
                id: companyId,
                smtpType: selectedEmailProvider.value,
                emailUser: emailForm.value.smtpUsername,
                port: emailForm.value.smtpPort,
                host: emailForm.value.smtpHost,
                password: emailForm.value.smtpPassword,
                microsoftEndpointUrl: selectedEmailProvider.value === PROVIDERS.MICROSOFT ? `https://login.microsoftonline.com/{tenantId}/oauth2/v2.0/token` : null,
                microsoftTenantId: emailForm.value.microsoftTenantId,
                microsoftClientId: emailForm.value.microsoftClientId,
                microsoftClientSecret: emailForm.value.microsoftClientSecret,
                updatedBy: 1,
                logoImage: `G:\\Loanmantra-backend\\workspace\\loanmantra-backend-qa\\HUDSON CAPITAL\\HUDSON.WebAPI\\bin\\Release\\net8.0\\AppDocs\\logo.svg`
            }
            const response = await updateSMTPDetailSystemParams(data);
            if (response?.status === 200) {
                showToaster(response?.message || "Email settings saved successfully", 'success');
                setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
                fetchEmailSettings();
            } else {
                showToaster(response?.message || "Failed to save email settings", 'error');
                setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
            }
        } catch (error) {
            console.error('Error saving email settings:', error);
            showToaster("Failed to save email settings", 'error');
            setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
        } finally {
            hideLoader();
        }
    };

    const fetchEmailSettings = async () => {
        try {
            showLoader();
            const response = await getSMTPDetailSystemParams(companyId);
            if (response?.status === 200) {
                const data = typeof response?.resultObject === 'string' && response?.resultObject.trim()?.length ? JSON.parse(response?.resultObject) : response?.resultObject || [];
                emailForm.reset({
                    // SMTP / Google
                    smtpHost: data?.host || null,
                    smtpPort: data?.port || null,
                    smtpUsername: data?.emailUser || null,
                    smtpPassword: data?.password || null,
                    useTLS: data?.useTLS || null,
                    // Microsoft
                    microsoftClientId: data?.microsoftClientId || null,
                    microsoftClientSecret: data?.microsoftClientSecret || null,
                    microsoftTenantId: data?.microsoftTenantId || null,
                    provider: data?.smtpType || PROVIDERS.GOOGLE,
                });
                setSelectedEmailProvider(emailProviderOptions.find(p => p.value === data?.smtpType) || emailProviderOptions.find(p => p.value === PROVIDERS.GOOGLE));
            } else {
                showToaster(response?.message || "Failed to get email settings", 'error');
                setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
            }
        } catch (error) {
            console.error('Error getting email settings:', error);
            showToaster("Failed to get email settings", 'error');
            setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
        } finally {
            hideLoader();
        }
    };

    useEffect(() => {
        applyValidatorsForProvider(selectedEmailProvider?.value);
    }, [selectedEmailProvider]);

    const closeModal = () => {
        onClose?.();
    };

    return (
        <>
            <div
                className={`modal-backdrop fade ${showAnimation ? 'show' : ''}`}
                onClick={() => onClose()}
                style={{ zIndex: 1040 }}
            ></div>
            <div
                className={`modal fade-right d-block drawer-modal transactions-drawer custom-full-width-drawer-size ${showAnimation ? 'show' : ''}`}
                id="transactionsModal"
                tabIndex={-1}
                role="dialog"
                aria-labelledby="transactionsModalLabel"
                style={{ zIndex: 1050 }}
                onClick={(e) => {
                    // Close modal when clicking outside the modal-content
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="drawer-head d-flex justify-content-between">
                            <div>
                                <h3>{companyName}</h3>
                                <span className="lm-text-light">Email Configuration</span>
                            </div>
                            <button type="button" className="close border-none" data-dismiss="modal" aria-label="Close" onClick={() => onClose()}>
                                <span aria-hidden="true"><img src={closePopupIcon} alt="" /></span>
                            </button>
                        </div>
                        <div className="drawer-body">
                            <div className="lm-card">
                                <div className="row">
                                    <div className="col-md-4 col-lg-3">
                                        <div className="form-group">
                                            <label htmlFor="email_provider">Email Provider</label>
                                            <Select
                                                className="basic-single"
                                                classNamePrefix="select"
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                }}
                                                options={sortSelectList(emailProviderOptions)}
                                                value={selectedEmailProvider}
                                                onChange={(option) => {
                                                    setSelectedEmailProvider(option);
                                                    emailForm.reset({ provider: option.value });
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="lm-divider my-3"></div>

                                <FieldGroup control={emailForm} render={({ get, invalid }) => (
                                    <form autoComplete="off">
                                        {/* SMTP Section */}
                                        {selectedEmailProvider?.value === PROVIDERS.SMTP && (
                                            <>
                                                <h3 className="lm-card-heading">SMTP Settings</h3>
                                                <div className="row">
                                                    <div className="col-md-4 col-lg-3">
                                                        <FieldControl name="smtpHost" render={({ handler, touched, hasError }) => (
                                                            <div className="form-group">
                                                                <label>SMTP Host <span className="asterisk">*</span></label>
                                                                <input type="text" className="form-control" placeholder="" {...handler()} />
                                                                <span className="error-message">{touched && hasError('required') && 'SMTP Host is required'}</span>
                                                                <span className="error-message">{touched && hasError('invalidHost') && 'SMTP Host cannot be empty'}</span>
                                                            </div>
                                                        )} />
                                                    </div>
                                                    <div className="col-md-4 col-lg-3">
                                                        <FieldControl name="smtpPort" render={({ handler, touched, hasError }) => (
                                                            <div className="form-group">
                                                                <label>SMTP Port <span className="asterisk">*</span></label>
                                                                <input type="number" className="form-control" placeholder="" {...handler()} />
                                                                <span className="error-message">{touched && hasError('required') && 'SMTP Port is required'}</span>
                                                                <span className="error-message">{touched && hasError('invalidPort') && 'SMTP Port must be a number between 1 and 65535'}</span>
                                                            </div>
                                                        )} />
                                                    </div>
                                                    <div className="col-md-4 col-lg-3">
                                                        <FieldControl name="smtpUsername" render={({ handler, touched, hasError }) => (
                                                            <div className="form-group">
                                                                <label>Username <span className="asterisk">*</span></label>
                                                                <input type="text" name="smtpUsername" className="form-control" placeholder="" {...handler()} autoComplete="new-email" />
                                                                <span className="error-message">{touched && hasError('required') && 'Username is required'}</span>
                                                            </div>
                                                        )} />
                                                    </div>
                                                    <div className="col-md-4 col-lg-3 input-password">
                                                        <FieldControl name="smtpPassword" render={({ handler, touched, hasError }) => {
                                                            const h = handler();
                                                            return (
                                                                <div className="form-group input-password">
                                                                    <label>Password <span className="asterisk">*</span></label>
                                                                    <input name="smtpPassword" id="smtp_password" type="password" className="form-control" placeholder="" {...h} autoComplete="new-password" />
                                                                    <img className="pass_eye" id="smtp_pass_eye" src={passwordEyeIcon} alt="" onClick={() => showPassword('smtp_password', 'smtp_pass_eye')} />
                                                                    <span className="error-message">{touched && hasError('required') && 'Password is required'}</span>
                                                                </div>
                                                            )
                                                        }} />
                                                    </div>
                                                    {/* <div className="col-md-4 col-lg-3">
                                                        <FieldControl name="useTLS" render={({ handler }) => (
                                                            <div className="form-group">
                                                                <label className="d-block mb-2">Use TLS/SSL (recommended)</label>
                                                                <div className="lm-toggle-switch">
                                                                    <input name="useTLS" type="checkbox" id="use_tls" {...handler()} checked={emailForm.value.useTLS} />
                                                                    <label htmlFor="use_tls">Status</label>
                                                                </div>
                                                            </div>
                                                        )} />
                                                    </div> */}
                                                </div>
                                            </>
                                        )}

                                        {/* Google Section - use SMTP credentials for Gmail */}
                                        {selectedEmailProvider?.value === PROVIDERS.GOOGLE && (
                                            <>
                                                <h3 className="lm-card-heading">Google Workspace / Gmail (SMTP)</h3>
                                                <div className="row">
                                                    <div className="col-md-4 col-lg-3">
                                                        <FieldControl name="smtpHost" render={({ handler, touched, hasError }) => (
                                                            <div className="form-group">
                                                                <label>SMTP Host <span className="asterisk">*</span></label>
                                                                <input type="text" className="form-control" placeholder="" {...handler()} />
                                                                <span className="error-message">{touched && hasError('required') && 'SMTP Host is required'}</span>
                                                                <span className="error-message">{touched && hasError('invalidHost') && 'SMTP Host cannot be empty'}</span>
                                                            </div>
                                                        )} />
                                                    </div>
                                                    <div className="col-md-4 col-lg-3">
                                                        <FieldControl name="smtpPort" render={({ handler, touched, hasError }) => (
                                                            <div className="form-group">
                                                                <label>SMTP Port <span className="asterisk">*</span></label>
                                                                <input type="number" className="form-control" placeholder="" {...handler()} />
                                                                <span className="error-message">{touched && hasError('required') && 'SMTP Port is required'}</span>
                                                                <span className="error-message">{touched && hasError('invalidPort') && 'SMTP Port must be a number between 1 and 65535'}</span>
                                                            </div>
                                                        )} />
                                                    </div>
                                                    <div className="col-md-4 col-lg-3">
                                                        <FieldControl name="smtpUsername" render={({ handler, touched, hasError }) => (
                                                            <div className="form-group">
                                                                <label>Username (email)</label>
                                                                <input type="text" name="smtpUsername" className="form-control" placeholder="" {...handler()} autoComplete="new-email" />
                                                                <span className="error-message">{touched && hasError('required') && 'Username is required'}</span>
                                                            </div>
                                                        )} />
                                                    </div>
                                                    <div className="col-md-4 col-lg-3 input-password">
                                                        <FieldControl name="smtpPassword" render={({ handler, touched, hasError }) => {
                                                            const h = handler();
                                                            return (
                                                                <div className="form-group input-password">
                                                                    <label>Password / App Password</label>
                                                                    <input id="smtp_password_gmail" name="smtpPassword" type="password" className="form-control" placeholder="" {...h} autoComplete="new-password" />
                                                                    <img className="pass_eye" id="smtp_pass_eye_gmail" src={passwordEyeIcon} alt="" onClick={() => showPassword('smtp_password_gmail', 'smtp_pass_eye_gmail')} />
                                                                    <span className="error-message">{touched && hasError('required') && 'Password is required'}</span>
                                                                </div>
                                                            )
                                                        }} />
                                                    </div>
                                                    {/* <div className="col-md-4 col-lg-3">
                                                        <FieldControl name="useTLS" render={({ handler }) => (
                                                            <div className="form-group">
                                                                <label className="d-block mb-2">Use TLS/SSL (recommended)</label>
                                                                <div className="lm-toggle-switch">
                                                                    <input type="checkbox" name="useTLS" id="use_tls_google" {...handler()} checked={emailForm.value.useTLS} />
                                                                    <label htmlFor="use_tls_google">Status</label>
                                                                </div>
                                                            </div>
                                                        )} />
                                                    </div> */}
                                                </div>
                                            </>
                                        )}

                                        {/* Microsoft Section (OAuth2 recommended) */}
                                        {selectedEmailProvider?.value === PROVIDERS.MICROSOFT && (
                                            <>
                                                <h3 className="lm-card-heading">Microsoft 365 / Outlook</h3>
                                                <div className="row">
                                                    <div className="col-md-4 col-lg-3">
                                                        <FieldControl name="smtpUsername" render={({ handler, touched, hasError }) => (
                                                            <div className="form-group">
                                                                <label>Username (email)</label>
                                                                <input type="text" name="smtpUsername" className="form-control" placeholder="" {...handler()} autoComplete="new-email" />
                                                                <span className="error-message">{touched && hasError('required') && 'Username is required'}</span>
                                                            </div>
                                                        )} />
                                                    </div>
                                                    <div className="col-md-4">
                                                        <FieldControl name="microsoftClientId" render={({ handler, touched, hasError }) => (
                                                            <div className="form-group">
                                                                <label>Client ID</label>
                                                                <input type="text" name="microsoftClientId" className="form-control" placeholder="" {...handler()} autoComplete="off" />
                                                                <span className="error-message">{touched && hasError('required') && 'Client ID is required'}</span>
                                                            </div>
                                                        )} />
                                                    </div>
                                                    <div className="col-md-4">
                                                        <FieldControl name="microsoftTenantId" render={({ handler, touched, hasError }) => (
                                                            <div className="form-group">
                                                                <label>Tenant ID</label>
                                                                <input type="text" name="microsoftTenantId" className="form-control" placeholder="" {...handler()} autoComplete="off" />
                                                                <span className="error-message">{touched && hasError('required') && 'Tenant ID is required'}</span>
                                                            </div>
                                                        )} />
                                                    </div>
                                                    <div className="col-md-4 input-password">
                                                        <FieldControl name="microsoftClientSecret" render={({ handler, touched, hasError }) => {
                                                            const h = handler();
                                                            return (
                                                                <div className="form-group input-password">
                                                                    <label>Client Secret (optional for auth code flow)</label>
                                                                    <input id="ms_client_secret" name="microsoftClientSecret" type="password" className="form-control" placeholder="" {...h} autoComplete="new-password" />
                                                                    <img className="pass_eye" id="ms_pass_eye" src={passwordEyeIcon} alt="" onClick={() => showPassword('ms_client_secret', 'ms_pass_eye')} />
                                                                    <span className="error-message">{touched && hasError('required') && 'Client Secret is required'}</span>
                                                                </div>
                                                            )
                                                        }} />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </form>
                                )
                                } />
                                <div className="sticky-btn-footer d-flex justify-content-end w-100 gap-15">
                                    <button type="button" className="lm-button2" onClick={closeModal}>
                                        Cancel
                                    </button>
                                    <button type="button" className="lm-button1" onClick={handleSave}>
                                        Save
                                    </button>
                                </div>
                            </div >
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SMTPSettings;
